/**
 * DisclaimerAck - reusable, config-driven claim disclaimer + acknowledgement.
 *
 * Renders a tenant-authored disclaimer (copy comes from the API, never
 * hardcoded) and, when the disclaimer requires it, a checkbox the user must
 * tick. The parent gates its submit/next button with `disclaimerBlocks(...)`
 * and records the acknowledgement as data via `disclaimerAckValue(...)`.
 *
 * Surface-agnostic: drop it on any claim submit/upload step (Sage, consumer,
 * cold/public). Returns null when no disclaimer is configured for the context.
 */

import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'
import { type DisclaimerConfig } from '@/shared/components/claims/disclaimer'

export interface DisclaimerAckProps {
  /** The configured disclaimer for this context, or null/undefined to render nothing. */
  disclaimer?: DisclaimerConfig | null
  acknowledged: boolean
  onAcknowledgedChange: (acknowledged: boolean) => void
  className?: string
  /**
   * True when the parent attempted to proceed while a required acknowledgement
   * was still unticked. Paints the error border + a failure message, mirroring
   * how the generated form surfaces a failed required field.
   */
  invalid?: boolean
  /** Failure message shown when `invalid`. Falls back to a generic prompt. */
  errorMessage?: string
}

export function DisclaimerAck({
  disclaimer,
  acknowledged,
  onAcknowledgedChange,
  className,
  invalid = false,
  errorMessage,
}: DisclaimerAckProps) {
  const { t } = useTranslation()
  if (!disclaimer) return null
  // Only a required-but-unticked acknowledgement can be in error.
  const showError = invalid && disclaimer.require_acknowledgement && !acknowledged
  return (
    <div className={className}>
      <div
        className={cn(
          'rounded-[8px] border-[0.5px] bg-[var(--bg-subtle)] p-4',
          showError ? 'border-[var(--status-error)]' : 'border-[var(--rule-strong)]',
        )}
      >
        {disclaimer.title && (
          <p className="m-0 mb-1.5 text-[12px] font-medium text-[var(--ink-1)]">
            {disclaimer.title}
          </p>
        )}
        <p className="m-0 whitespace-pre-line text-[12px] leading-[1.6] text-[var(--ink-2)]">
          {disclaimer.text}
        </p>
        {disclaimer.require_acknowledgement && (
          <Label className="mt-3 flex cursor-pointer items-start gap-2 font-normal">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(v) => onAcknowledgedChange(v === true)}
              aria-invalid={showError || undefined}
              aria-required
              className={cn('mt-0.5', showError && 'border-[var(--status-error)]')}
            />
            <span className="text-[12px] leading-[1.5] text-[var(--ink-1)]">
              {disclaimer.acknowledgement_label}
              <span className="ml-0.5 text-[var(--status-error)]">*</span>
            </span>
          </Label>
        )}
      </div>
      {showError && (
        <p className="mt-1 text-[11px] text-[var(--status-error)]">
          {errorMessage || t('common.mustConfirmToContinue')}
        </p>
      )}
    </div>
  )
}
